import {radialProgressiveBlur} from '@remotion/effects/radial-progressive-blur';
import {MacOSCursor} from '@remotion/mac-cursors';
import {Video} from '@remotion/media';
import {
	AbsoluteFill,
	Composition,
	Easing,
	HtmlInCanvas,
	Interactive,
	interpolate,
	useCurrentFrame,
	useVideoConfig,
} from 'remotion';

const BasicsSeriesTrimInner = ({
	captureStyle,
}: {
	readonly captureStyle: React.CSSProperties | null;
}) => {
	const frame = useCurrentFrame();
	const {fps} = useVideoConfig();

	const screenCapture = (
		<>
			<Video
				name="Screen recording"
				premountFor={fps}
				muted
				effects={
					HtmlInCanvas.isSupported()
						? []
						: [
								radialProgressiveBlur({
									center: [0.77, 0.48],
									width: 1.3,
									height: 1.1,
									start: 0.44,
									startBlur: 0,
									endBlur: 26,
								}),
							]
				}
				src={
					'https://remotion.media/studio-close-ups/basics-showcase/trimming-series-aef2fd04f359.mp4'
				}
				durationInFrames={463}
				style={{
					position: 'absolute',
				}}
			/>
			<MacOSCursor
				name="Recorded cursor"
				premountFor={fps}
				cursor={interpolate(
					frame,
					[32, 111, 125, 126, 382],
					['auto', 'custom', 'auto', 'custom', 'auto'],
					{
						easing: Easing.step1,
						extrapolateLeft: 'clamp',
						extrapolateRight: 'clamp',
					},
				)}
				customCursor={
					'url("data:image/svg+xml,%3Csvg%20width%3D%2224%22%20height%3D%2224%22%20viewBox%3D%220%200%2032%2032%22%20fill%3D%22none%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%0A%3Cg%20filter%3D%22url(%23shadow)%22%3E%0A%3Cpath%20d%3D%22M7%204.5H11C12.1%204.5%2013%205.4%2013%206.5V25.5C13%2026.6%2012.1%2027.5%2011%2027.5H7M25%204.5H21C19.9%204.5%2019%205.4%2019%206.5V25.5C19%2026.6%2019.9%2027.5%2021%2027.5H25%22%20stroke%3D%22white%22%20stroke-width%3D%225%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3Cpath%20d%3D%22M7%204.5H11C12.1%204.5%2013%205.4%2013%206.5V25.5C13%2026.6%2012.1%2027.5%2011%2027.5H7M25%204.5H21C19.9%204.5%2019%205.4%2019%206.5V25.5C19%2026.6%2019.9%2027.5%2021%2027.5H25%22%20stroke%3D%22black%22%20stroke-width%3D%222.5%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%0A%3C%2Fg%3E%0A%3Cdefs%3E%0A%3Cfilter%20id%3D%22shadow%22%20x%3D%220%22%20y%3D%220%22%20width%3D%2232%22%20height%3D%2232%22%20filterUnits%3D%22userSpaceOnUse%22%3E%0A%3CfeDropShadow%20dx%3D%220%22%20dy%3D%221.5%22%20stdDeviation%3D%221%22%20flood-color%3D%22black%22%20flood-opacity%3D%220.35%22%2F%3E%0A%3C%2Ffilter%3E%0A%3C%2Fdefs%3E%0A%3C%2Fsvg%3E") 12 12, ew-resize'
				}
				style={{
					position: 'absolute',
					left: 0,
					top: 0,
					scale: interpolate(
						frame,
						[0, 150, 346, 419, 425],
						[
							3.503549739115559, 3.1531947652040033, 3.503549739115559,
							3.1531947652040033, 3.503549739115559,
						],
						{
							easing: Easing.step1,
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
					translate: interpolate(
						frame,
						[
							32, 33, 34, 35, 36, 37, 38, 39, 40, 41, 42, 43, 45, 46, 47, 48,
							49, 50, 51, 52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 62, 63, 64,
							65, 66, 67, 68, 69, 70, 71, 72, 73, 74, 75, 76, 77, 79, 80, 92,
							93, 94, 95, 96, 97, 98, 99, 100, 101, 102, 103, 104, 105, 106,
							107, 108, 109, 110, 111, 112, 113, 114, 115, 116, 117, 118, 119,
							120, 121, 122, 123, 124, 125, 126, 127, 128, 129, 130, 131, 132,
							133, 134, 135, 136, 137, 138, 139, 140, 141, 142, 143, 146, 147,
							179, 180, 182, 183, 184, 185, 186, 187, 188, 189, 190, 191, 192,
							193, 194, 195, 196, 197, 198, 199, 200, 201, 202, 203, 204, 205,
							206, 207, 208, 209, 210, 211, 212, 213, 214, 215, 216, 217, 218,
							219, 220, 221, 222, 223, 224, 225, 226, 227, 228, 229, 230, 231,
							232, 233, 234, 235, 236, 237, 238, 239, 240, 241, 242, 243, 244,
							245, 246, 247, 248, 249, 250, 251, 252, 253, 254, 255, 256, 257,
							258, 259, 260, 261, 262, 264, 265, 266, 267, 270, 271, 274, 275,
							278, 279, 281, 282, 301, 302, 316, 317, 344, 345, 348, 349, 380,
							381, 382, 383, 384, 385, 386, 387, 388, 389, 390, 391, 392, 393,
							394, 395, 396, 397, 398, 399, 400, 401, 402, 403, 404, 405, 406,
							407, 408, 409, 410, 411, 412, 413, 414,
						],
						[
							'2336.8676759900777px 752.7568214866136px',
							'2334.5137285091096px 742.0272004105723px',
							'2325.1253100675735px 727.9582584894363px',
							'2304.979899067659px 701.0794628346591px',
							'2284.8344880677446px 674.200667179882px',
							'2261.322384740399px 650.6885638525362px',
							'2249.073646394663px 638.4398255068002px',
							'2238.3440253186213px 630.0504661705586px',
							'2232.801300145411px 624.5077409973484px',
							'2227.7649473954325px 621.3189633051065px',
							'2225.3151997262853px 618.8692156359593px',
							'2225.3151997262853px 617.7606706013172px',
							'2225.3151997262853px 617.7606706013172px',
							'2225.3151997262853px 616.8026687195279px',
							'2224.206654691643px 614.1202634505175px',
							'2221.0178769994013px 609.0839107005389px',
							'2219.1703019416645px 605.3887605850655px',
							'2211.273629287486px 592.8115644512873px',
							'2205.7309041142757px 585.4212642203404px',
							'2203.8833290565394px 581.726114104867px',
							'2200.188178941066px 574.3358138739201px',
							'2193.3042511333506px 567.4518860662048px',
							'2190.1154734411084px 562.4155333162262px',
							'2188.7742708066035px 559.7331280472158px',
							'2187.433068172098px 557.0507227782055px',
							'2186.091865537593px 554.3683175091951px',
							'2184.7506629030877px 553.0271148746899px',
							'2184.7506629030877px 550.5773672055427px',
							'2184.7506629030877px 549.4688221709007px',
							'2184.7506629030877px 549.4688221709007px',
							'2184.7506629030877px 547.101188948764px',
							'2184.7506629030877px 545.992643914122px',
							'2184.7506629030877px 544.8840988794799px',
							'2184.7506629030877px 542.4343512103327px',
							'2184.7506629030877px 542.4343512103327px',
							'2184.7506629030877px 539.9846035411855px',
							'2184.7506629030877px 537.3021982721751px',
							'2184.7506629030877px 534.8524506030279px',
							'2184.7506629030877px 532.1700453340176px',
							'2184.7506629030877px 531.0615002993756px',
							'2184.7506629030877px 528.6117526302284px',
							'2184.7506629030877px 528.6117526302284px',
							'2184.7506629030877px 526.3946625609443px',
							'2184.7506629030877px 526.3946625609443px',
							'2184.7506629030877px 525.4229749379865px',
							'2184.7506629030877px 525.4229749379865px',
							'2184.7506629030877px 524.4923445385339px',
							'2184.7506629030877px 524.4923445385339px',
							'2184.7506629030877px 522.7268839278078px',
							'2184.7506629030877px 521.6183388931657px',
							'2184.7506629030877px 519.4012488238816px',
							'2181.0555127876146px 518.0600461893764px',
							'2179.714310153109px 516.7188435548712px',
							'2179.714310153109px 515.377640920366px',
							'2179.714310153109px 514.269095885724px',
							'2178.3731075186042px 511.8193482165769px',
							'2177.031904884099px 510.4781455820717px',
							'2175.690702249594px 507.79574031306134px',
							'2174.3494996150885px 505.113335044051px',
							'2173.0082969805835px 502.4309297750406px',
							'2169.8195192883413px 497.394577025062px',
							'2168.4783166538364px 494.71217175605165px',
							'2167.137114019331px 492.02976648704134px',
							'2165.795911384826px 489.347361218031px',
							'2164.4547087503206px 484.31100846805236px',
							'2163.1135061158157px 481.628603199042px',
							'2161.7723034813102px 480.2874005645368px',
							'2158.5835257890685px 473.909845180053px',
							'2157.2423231545636px 471.22743991104267px',
							'2155.901120520058px 468.54503464203236px',
							'2154.559917885553px 465.862629373022px',
							'2153.218715251048px 464.5214267385168px',
							'2151.877512616543px 461.83902146950646px',
							'2150.7689675819006px 459.38927380035926px',
							'2149.660422547259px 458.28072876571724px',
							'2149.660422547259px 457.17218373107517px',
							'2148.3192199127534px 455.83098109657004px',
							'2147.2106748781116px 453.3812334274228px',
							'2147.2106748781116px 450.6988281584125px',
							'2145.869472243606px 449.3576255239073px',
							'2144.5282696091012px 448.0164228894021px',
							'2144.5282696091012px 445.33401762039176px',
							'2143.187066974596px 443.9928149858866px',
							'2141.845864340091px 441.3104097168762px',
							'2141.845864340091px 438.860662047729px',
							'2140.5046617055855px 437.51945941322384px',
							'2140.5046617055855px 436.4109143785818px',
							'2139.3961166709437px 436.4109143785818px',
							'2139.3961166709437px 433.9611667094346px',
							'2139.3961166709437px 433.9611667094346px',
							'2139.3961166709437px 432.98947908647676px',
							'2138.0549140364383px 431.6482764519716px',
							'2138.0549140364383px 431.6482764519716px',
							'2138.0549140364383px 430.6765888290138px',
							'2138.0549140364383px 430.6765888290138px',
							'2138.0549140364383px 429.71858694722437px',
							'2138.0549140364383px 429.71858694722437px',
							'2136.9463690017965px 429.71858694722437px',
							'2136.9463690017965px 428.69215635959284px',
							'2136.9463690017965px 428.69215635959284px',
							'2136.9463690017965px 427.78889744247715px',
							'2136.9463690017965px 427.78889744247715px',
							'2136.522111025575px 427.78889744247715px',
							'2136.522111025575px 427.78889744247715px',
							'2134.66085022667px 427.78889744247715px',
							'2134.66085022667px 427.78889744247715px',
							'2133.6617911213752px 427.78889744247715px',
							'2132.5258746043964px 427.78889744247715px',
							'2132.5258746043964px 427.78889744247715px',
							'2130.1719271234283px 428.8563852536139px',
							'2128.0232657599863px 429.32170045334016px',
							'2128.0232657599863px 429.32170045334016px',
							'2125.8472329142073px 429.91018732358225px',
							'2125.8472329142073px 429.91018732358225px',
							'2121.850996493029px 431.0050466170559px',
							'2115.0218116499873px 431.01873235822427px',
							'2100.5149260114617px 432.6747070396031px',
							'2100.5149260114617px 432.6747070396031px',
							'2085.9806688905996px 432.8115644512873px',
							'2066.6427166196218px 432.8115644512873px',
							'2052.9432897100332px 432.8115644512873px',
							'2052.9432897100332px 432.8115644512873px',
							'2047.5511076896757px 432.8115644512873px',
							'2043.1853562569497px 432.8115644512873px',
							'2043.1853562569497px 432.8115644512873px',
							'2041.8304678812763px 432.8115644512873px',
							'2038.107946283466px 432.8115644512873px',
							'2038.107946283466px 432.8115644512873px',
							'2034.3169959798136px 432.8115644512873px',
							'2031.5661620049611px 432.8115644512873px',
							'2028.692156359593px 432.8115644512873px',
							'2028.692156359593px 432.8115644512873px',
							'2026.4066375844668px 432.8115644512873px',
							'2025.3938927380036px 432.8115644512873px',
							'2025.3938927380036px 432.8115644512873px',
							'2024.2853477033616px 432.8115644512873px',
							'2023.2178598922249px 432.8115644512873px',
							'2023.2178598922249px 432.8115644512873px',
							'2022.2324865280987px 432.8115644512873px',
							'2022.2324865280987px 432.8115644512873px',
							'2021.2607989051407px 432.8115644512873px',
							'2021.2607989051407px 432.8115644512873px',
							'2019.2900521768881px 432.8115644512873px',
							'2019.2900521768881px 432.8115644512873px',
							'2018.2773073304252px 432.8115644512873px',
							'2017.1550765546147px 432.8115644512873px',
							'2016.0054742964674px 432.8115644512873px',
							'2014.7463861089727px 432.8115644512873px',
							'2011.434436746215px 432.8115644512873px',
							'2008.6699170301943px 432.8115644512873px',
							'2005.8095971259943px 432.8115644512873px',
							'2002.8808485159525px 432.8115644512873px',
							'2002.8808485159525px 432.8115644512873px',
							'1999.1309554358054px 432.8115644512873px',
							'1996.7496364725002px 432.8115644512873px',
							'1992.6302283808059px 432.8115644512873px',
							'1992.6302283808059px 432.8115644512873px',
							'1988.332905653922px 432.8115644512873px',
							'1985.636814643743px 432.8115644512873px',
							'1985.636814643743px 432.8115644512873px',
							'1982.1058934222906px 432.8115644512873px',
							'1977.849627918912px 432.8115644512873px',
							'1974.9619365323754px 432.8115644512873px',
							'1974.9619365323754px 432.8115644512873px',
							'1972.4163886750491px 432.8115644512873px',
							'1970.2540415704389px 432.8115644512873px',
							'1970.2540415704389px 432.8115644512873px',
							'1969.1181250534598px 432.8115644512873px',
							'1967.9685227953125px 432.8115644512873px',
							'1966.942092207681px 432.8115644512873px',
							'1965.9840903258917px 432.8115644512873px',
							'1965.9840903258917px 432.8115644512873px',
							'1964.8892310324181px 432.8115644512873px',
							'1962.5900265161235px 432.8115644512873px',
							'1962.5900265161235px 432.8115644512873px',
							'1961.467795740313px 432.8115644512873px',
							'1959.4286203062184px 432.8115644512873px',
							'1959.4286203062184px 432.8115644512873px',
							'1958.306389530408px 432.8115644512873px',
							'1957.1841587545975px 432.8115644512873px',
							'1957.1841587545975px 432.8115644512873px',
							'1956.1850996493029px 432.8115644512873px',
							'1956.1850996493029px 432.8115644512873px',
							'1955.2134120263452px 432.8115644512873px',
							'1955.2134120263452px 432.8115644512873px',
							'1954.2554101445558px 432.8115644512873px',
							'1954.2554101445558px 432.8115644512873px',
							'1953.3110940039346px 432.8115644512873px',
							'1953.3110940039346px 432.8115644512873px',
							'1952.3530921221452px 432.8115644512873px',
							'1952.3530921221452px 432.8115644512873px',
							'1951.4498332050296px 432.8115644512873px',
							'1951.4498332050296px 432.8115644512873px',
							'1950.9708322641347px 433.24950816867675px',
							'1950.9708322641347px 433.24950816867675px',
							'1950.4918313232401px 433.6874518860662px',
							'1950.4918313232401px 433.6874518860662px',
							'1949.1643144299035px 433.6874518860662px',
							'1949.1643144299035px 433.6874518860662px',
							'1947.850483277735px 433.6874518860662px',
							'1947.850483277735px 433.6874518860662px',
							'1947.8231117953983px 433.6874518860662px',
							'1947.8231117953983px 433.6874518860662px',
							'1948.959028312377px 433.6874518860662px',
							'1948.959028312377px 433.6874518860662px',
							'1952.4625780514925px 436.3151141904029px',
							'1975.9473098965016px 459.79984603541186px',
							'2007.8898297835942px 495.10905824993586px',
							'2051.656830040202px 538.8760585065435px',
							'2086.966042254726px 570.8185783936361px',
							'2122.27525446925px 606.1277906081601px',
							'2155.025233085279px 635.018390214695px',
							'2207.099478231118px 679.3738773415448px',
							'2233.9509024035583px 702.8586091865537px',
							'2255.9165169788726px 721.4575314344368px',
							'2266.618766572577px 729.8195192883414px',
							'2274.9670686853133px 735.8275596612779px',
							'2279.9760499529552px 738.9889658711829px',
							'2282.6310837396286px 741.6439996578565px',
							'2285.2861175263024px 742.9715165511932px',
							'2286.3809768197757px 744.29903344453px',
							'2287.4758361132494px 744.29903344453px',
							'2288.570695406723px 745.3938927380036px',
							'2291.2257291933965px 746.7214096313404px',
							'2293.8807629800704px 748.0489265246771px',
							'2296.5357967667437px 749.3764434180139px',
							'2299.190830553417px 749.3764434180139px',
							'2301.845864340091px 750.7039603113507px',
							'2304.5008981267642px 750.7039603113507px',
							'2309.5098793944057px 752.0314772046873px',
							'2316.872808143016px 753.8653665212556px',
							'2324.235736891626px 757.5331451543923px',
							'2331.598665640236px 759.3670344709606px',
							'2342.643058763151px 764.8687024206655px',
							'2347.652040030793px 768.0301086305706px',
							'2350.3070738174665px 770.685142417244px',
							'2352.96210760414px 772.0126593105808px',
							'2355.6171413908137px 773.3401762039175px',
							'2356.94465828415px 774.6676930972543px',
						],
						{
							extrapolateLeft: 'clamp',
							extrapolateRight: 'clamp',
						},
					),
				}}
			/>
		</>
	);

	return (
		<AbsoluteFill
			showInTimeline={false}
			style={{backgroundColor: '#111518', overflow: 'hidden'}}
		>
			{HtmlInCanvas.isSupported() ? (
				<HtmlInCanvas
					name="Canvas Capture"
					width={2560}
					height={924}
					effects={[
						radialProgressiveBlur({
							center: [0.77, 0.48],
							width: 1.3,
							height: 1.1,
							start: 0.44,
							startBlur: 0,
							endBlur: 26,
						}),
					]}
					durationInFrames={463}
					premountFor={fps}
					style={{
						translate: '-830px 150px',
						scale: 0.9,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</HtmlInCanvas>
			) : (
				<AbsoluteFill
					name="Canvas Capture"
					durationInFrames={463}
					premountFor={fps}
					style={{
						width: 2560,
						height: 924,
						translate: '-830px 150px',
						scale: 0.9,
						rotate: '-2deg',
						transformOrigin: '0 0',
						...captureStyle,
					}}
				>
					{screenCapture}
				</AbsoluteFill>
			)}
		</AbsoluteFill>
	);
};

export const BasicsSeriesTrimPreview = Interactive.withSchema({
	Component: BasicsSeriesTrimInner,
	componentName: 'BasicsSeriesTrimPreview',
	schema: {},
	wrapInSequence: true,
	layout: 'absolute-fill',
});

export const BasicsSeriesTrimComposition = () => {
	return (
		<Composition
			id="BasicsSeriesTrim"
			component={BasicsSeriesTrimPreview}
			width={1920}
			height={1080}
			defaultProps={{captureStyle: null}}
			fps={60}
			durationInFrames={463}
		/>
	);
};
